import type {
  Account,
  BrandKit,
  Domain,
  EventRow,
  Experiment,
  Funnel,
  GeoRow,
  Insight,
  Kpi,
  NaraBlock,
  OvernightItem,
  Point,
  Property,
  ReplaySession,
  Row,
  Template,
} from "./types";

/* ---------- deterministic noise so charts look designed, not random ------- */
function mulberry32(seed: number) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 12 }, (_, i) => `${(i * 2).toString().padStart(2, "0")}`);

/** Smooth-ish series with a gentle upward bias. */
export function genSeries(
  seed: number,
  base: number,
  spread: number,
  labels: string[] = WEEK,
): Point[] {
  const rnd = mulberry32(seed);
  let v = base;
  return labels.map((t, i) => {
    v += (rnd() - 0.42) * spread + (base * 0.012 * i) / labels.length;
    return { t, v: Math.max(0, Math.round(v)) };
  });
}

export const properties: Property[] = [
  { id: "aigen-web", name: "aigen.solutions", domain: "aigen.solutions", kind: "web", hue: "#B84422" },
  { id: "studio-landing", name: "Studio launch", domain: "studio.aigen.solutions", kind: "web", hue: "#C8923D" },
  { id: "nara-waitlist", name: "Nara waitlist", domain: "meet-nara.com", kind: "web", hue: "#3E7C74" },
  { id: "ios-app", name: "aigen iOS", domain: "App Store", kind: "ios", hue: "#5B6470" },
];

/* ---------------------------- Today / Signals KPIs ------------------------ */
export const todayKpis: Kpi[] = [
  {
    key: "visitors",
    label: "Visitors today",
    value: 4218,
    unit: "count",
    delta: 12.4,
    trend: "up",
    series: genSeries(11, 220, 90, HOURS),
    source: "posthog",
    focal: true,
  },
  {
    key: "conversions",
    label: "Conversions",
    value: 186,
    unit: "count",
    delta: 8.1,
    trend: "up",
    series: genSeries(22, 9, 6, HOURS),
    source: "posthog",
  },
  {
    key: "convrate",
    label: "Conversion rate",
    value: 4.4,
    unit: "pct",
    delta: 0.6,
    trend: "up",
    series: genSeries(33, 4, 1.5, HOURS),
    source: "matomo",
  },
  {
    key: "avgtime",
    label: "Avg. time on site",
    value: 142,
    unit: "sec",
    delta: -3.2,
    trend: "down",
    series: genSeries(44, 130, 30, HOURS),
    source: "matomo",
  },
];

export const signalsKpis: Kpi[] = [
  {
    key: "visitors",
    label: "Visitors",
    value: 28940,
    unit: "count",
    delta: 14.2,
    trend: "up",
    series: genSeries(101, 3600, 700),
    source: "posthog",
    focal: true,
  },
  {
    key: "pageviews",
    label: "Pageviews",
    value: 71204,
    unit: "count",
    delta: 9.8,
    trend: "up",
    series: genSeries(102, 9200, 1500),
    source: "matomo",
  },
  {
    key: "conv",
    label: "Conversions",
    value: 1284,
    unit: "count",
    delta: 18.5,
    trend: "up",
    series: genSeries(103, 150, 50),
    source: "posthog",
  },
  {
    key: "bounce",
    label: "Bounce rate",
    value: 38.2,
    unit: "pct",
    delta: -4.1,
    trend: "up",
    series: genSeries(104, 42, 6),
    source: "matomo",
  },
];

/** Main Signals overview chart — visitors vs conversions (gold = single 2nd series). */
export const overviewVisitors = genSeries(201, 3600, 700);
export const overviewConversions = genSeries(202, 150, 60).map((p) => ({
  ...p,
  v: Math.round(p.v),
}));

export const topPages: Row[] = [
  { label: "/", value: 18420, pct: 1, sub: "Home", source: "matomo" },
  { label: "/pricing", value: 9240, pct: 0.5, sub: "Pricing", source: "matomo" },
  { label: "/studio", value: 6110, pct: 0.33, sub: "Studio launch", source: "matomo" },
  { label: "/nara", value: 4980, pct: 0.27, sub: "Meet Nara", source: "matomo" },
  { label: "/blog/replay", value: 2870, pct: 0.16, sub: "Mobile replay", source: "matomo" },
];

export const topSources: Row[] = [
  { label: "Direct", value: 11200, pct: 1, source: "posthog" },
  { label: "Google", value: 8430, pct: 0.75, sub: "organic", source: "posthog" },
  { label: "Product Hunt", value: 3920, pct: 0.35, source: "posthog" },
  { label: "LinkedIn", value: 2610, pct: 0.23, source: "posthog" },
  { label: "X / Twitter", value: 1740, pct: 0.16, source: "posthog" },
];

export const channels: Row[] = [
  { label: "Organic search", value: 9800, pct: 1, sub: "+11%" },
  { label: "Direct", value: 8200, pct: 0.84, sub: "+4%" },
  { label: "Referral", value: 5100, pct: 0.52, sub: "+22%" },
  { label: "Social", value: 3400, pct: 0.35, sub: "+9%" },
  { label: "Email", value: 1900, pct: 0.19, sub: "−2%" },
  { label: "Paid", value: 1200, pct: 0.12, sub: "+1%" },
];

export const devices: Row[] = [
  { label: "Mobile", value: 16200, pct: 0.56, sub: "iOS 62% · Android 38%" },
  { label: "Desktop", value: 10100, pct: 0.35, sub: "Chrome 71%" },
  { label: "Tablet", value: 2640, pct: 0.09, sub: "iPad" },
];

export const browsers: Row[] = [
  { label: "Chrome", value: 14800, pct: 1 },
  { label: "Safari", value: 8900, pct: 0.6 },
  { label: "Firefox", value: 2100, pct: 0.14 },
  { label: "Edge", value: 1600, pct: 0.11 },
];

export const geo: GeoRow[] = [
  { code: "US", label: "United States", value: 9800, pct: 1 },
  { code: "GB", label: "United Kingdom", value: 4200, pct: 0.43 },
  { code: "DE", label: "Germany", value: 3100, pct: 0.32 },
  { code: "AZ", label: "Azerbaijan", value: 2400, pct: 0.24 },
  { code: "IN", label: "India", value: 2100, pct: 0.21 },
  { code: "CA", label: "Canada", value: 1700, pct: 0.17 },
  { code: "FR", label: "France", value: 1500, pct: 0.15 },
];

export const behaviorRows: Row[] = [
  { label: "Avg. scroll depth", value: 68, pct: 0.68, sub: "of page" },
  { label: "Avg. session", value: 142, pct: 0.5, sub: "seconds" },
  { label: "Pages / session", value: 2.4, pct: 0.4, sub: "" },
  { label: "Rage clicks", value: 38, pct: 0.1, sub: "on /pricing" },
];

export const funnels: Funnel[] = [
  {
    name: "Landing → Sign-up",
    source: "posthog",
    steps: [
      { label: "Visited landing", value: 28940 },
      { label: "Clicked CTA", value: 9870 },
      { label: "Started form", value: 4120 },
      { label: "Submitted", value: 1284 },
    ],
  },
  {
    name: "Pricing → Checkout",
    source: "posthog",
    steps: [
      { label: "Viewed pricing", value: 9240 },
      { label: "Selected plan", value: 3110 },
      { label: "Reached checkout", value: 1420 },
      { label: "Purchased", value: 612 },
    ],
  },
];

export const events: EventRow[] = [
  { name: "$pageview", count: 71204, delta: 9.8, lastSeen: "now", kind: "pageview" },
  { name: "cta_click", count: 9870, delta: 14.1, lastSeen: "12s ago", kind: "autocapture" },
  { name: "signup_started", count: 4120, delta: 6.2, lastSeen: "44s ago", kind: "custom" },
  { name: "signup_completed", count: 1284, delta: 18.5, lastSeen: "2m ago", kind: "custom" },
  { name: "pricing_viewed", count: 9240, delta: -1.4, lastSeen: "1m ago", kind: "custom" },
  { name: "video_played", count: 3380, delta: 22.0, lastSeen: "5m ago", kind: "autocapture" },
  { name: "form_error", count: 210, delta: -33.0, lastSeen: "9m ago", kind: "custom" },
];

export const replays: ReplaySession[] = [
  { id: "r1", kind: "mobile", device: "iPhone 15 Pro", os: "iOS 18.2", location: "Austin, US", startedAt: "8m ago", durationSec: 214, pages: 5, events: 41, activity: 86, flag: "rage tap · /pricing", source: "posthog" },
  { id: "r2", kind: "web", device: "MacBook Pro", os: "macOS", browser: "Chrome 126", location: "London, GB", startedAt: "14m ago", durationSec: 332, pages: 7, events: 63, activity: 72, source: "posthog" },
  { id: "r3", kind: "mobile", device: "Pixel 8", os: "Android 15", location: "Berlin, DE", startedAt: "22m ago", durationSec: 96, pages: 2, events: 18, activity: 40, flag: "dead click", source: "posthog" },
  { id: "r4", kind: "web", device: "ThinkPad", os: "Windows", browser: "Edge", location: "Baku, AZ", startedAt: "31m ago", durationSec: 458, pages: 9, events: 88, activity: 91, source: "posthog" },
  { id: "r5", kind: "mobile", device: "iPhone 13", os: "iOS 17.5", location: "Toronto, CA", startedAt: "47m ago", durationSec: 142, pages: 3, events: 27, activity: 55, source: "posthog" },
  { id: "r6", kind: "web", device: "iMac", os: "macOS", browser: "Safari 17", location: "Paris, FR", startedAt: "1h ago", durationSec: 276, pages: 6, events: 49, activity: 64, source: "posthog" },
];

/* ------------------------------- Nara layer ------------------------------- */
export const insights: Insight[] = [
  {
    id: "i1",
    severity: "down",
    metric: "Mobile bounce",
    title: "Mobile bounce on /pricing is up 19%",
    detail: "iPhone visitors leave within 6s — the plan table overflows below the fold.",
    suggestion: "Let Nara rebuild the pricing table as stacked cards for mobile.",
    page: "/pricing",
    makePrompt:
      "Rebuild the /pricing plan comparison as stacked, thumb-friendly cards for mobile so it stops overflowing below the fold. Keep the three tiers and the coral 'Most popular' badge.",
  },
  {
    id: "i2",
    severity: "up",
    metric: "Conversions",
    title: "The new hero lifted sign-ups 12%",
    detail: "Variant B (\"See what's working\") is beating control with 96% confidence.",
    suggestion: "Roll variant B to 100% of traffic.",
    page: "/",
    makePrompt: "Promote A/B variant B of the homepage hero to 100% of traffic.",
  },
  {
    id: "i3",
    severity: "info",
    metric: "Acquisition",
    title: "Product Hunt traffic is converting 2.3×",
    detail: "PH visitors convert far above average but only see the generic homepage.",
    suggestion: "Make a dedicated Product Hunt landing page.",
    page: "/",
    makePrompt:
      "Make a dedicated landing page for Product Hunt visitors with a 'Hello, hunters' headline, the launch-day offer, and social proof from PH.",
  },
];

export const overnight: OvernightItem[] = [
  { id: "o1", kind: "published", title: "Published variant B of the homepage hero", detail: "Now live for 50% of traffic", time: "02:14" },
  { id: "o2", kind: "flagged", title: "Flagged 2 anomalies", detail: "Mobile bounce on /pricing · form errors down 33%", time: "03:40" },
  { id: "o3", kind: "drafted", title: "Drafted a Product Hunt campaign", detail: "Landing page + waitlist form, ready for your review", time: "04:55" },
  { id: "o4", kind: "won", title: "Pricing test reached significance", detail: "Stacked cards won by +14% — ready to ship", time: "05:30" },
];

export const naraSuggestions = [
  "A landing page for our Series A announcement",
  "An A/B test of the pricing headline",
  "A waitlist form for the iOS app",
  "A Product Hunt launch page",
];

export const naraSeed: NaraBlock[] = [
  { id: "b1", type: "hero", title: "Hero", summary: "\"See what's working\" + coral CTA, warm cream" },
  { id: "b2", type: "features", title: "Features", summary: "Three columns: Signals, Make, Library" },
  { id: "b3", type: "testimonial", title: "Social proof", summary: "Quote from a marketing lead" },
  { id: "b4", type: "pricing", title: "Pricing", summary: "Three tiers, stacked on mobile" },
  { id: "b5", type: "cta", title: "Closing CTA", summary: "\"Start making\" — one coral button" },
];

/* ------------------------------ Experiments ------------------------------- */
export const experiments: Experiment[] = [
  {
    id: "e1",
    name: "Homepage hero copy",
    status: "running",
    page: "/",
    startedAt: "3 days ago",
    confidence: 96,
    variants: [
      { name: "A — control", traffic: 0.5, visitors: 7210, conversion: 4.0, uplift: 0, control: true },
      { name: "B — \"See what's working\"", traffic: 0.5, visitors: 7180, conversion: 4.5, uplift: 12.0 },
    ],
  },
  {
    id: "e2",
    name: "Pricing layout",
    status: "won",
    page: "/pricing",
    startedAt: "9 days ago",
    confidence: 99,
    variants: [
      { name: "A — table", traffic: 0.5, visitors: 4600, conversion: 6.1, uplift: 0, control: true },
      { name: "B — stacked cards", traffic: 0.5, visitors: 4640, conversion: 7.0, uplift: 14.0 },
    ],
  },
  {
    id: "e3",
    name: "CTA color",
    status: "draft",
    page: "/",
    startedAt: "—",
    confidence: 0,
    variants: [
      { name: "A — coral", traffic: 0.5, visitors: 0, conversion: 0, uplift: 0, control: true },
      { name: "B — ink", traffic: 0.5, visitors: 0, conversion: 0, uplift: 0 },
    ],
  },
];

/* -------------------------------- Library --------------------------------- */
export const templates: Template[] = [
  { id: "t1", name: "Series A announcement", kind: "prebuilt", category: "Launch", blurb: "Bold news hero + investor logos", thumb: ["#B84422", "#C8923D"] },
  { id: "t2", name: "Waitlist", kind: "prebuilt", category: "Capture", blurb: "Single field, social proof", thumb: ["#3E7C74", "#86B0A8"] },
  { id: "t3", name: "Pricing — stacked", kind: "prebuilt", category: "Pricing", blurb: "Mobile-first tier cards", thumb: ["#1A1815", "#5B6470"] },
  { id: "t4", name: "Event registration", kind: "prebuilt", category: "Event", blurb: "Agenda + speakers + RSVP", thumb: ["#C8923D", "#E4C58A"] },
  { id: "t5", name: "Product Hunt launch", kind: "custom", category: "Launch", blurb: "Made by Nara · last night", thumb: ["#B84422", "#7A2A14"], updated: "6h ago" },
  { id: "t6", name: "Homepage v3", kind: "published", category: "Home", blurb: "Live · aigen.solutions", thumb: ["#3E7C74", "#1A1815"], updated: "live", views: 28940 },
  { id: "t7", name: "Nara waitlist", kind: "published", category: "Capture", blurb: "Live · meet-nara.com", thumb: ["#5B6470", "#C0BEB6"], updated: "live", views: 4980 },
  { id: "t8", name: "Changelog", kind: "prebuilt", category: "Content", blurb: "Dated entries, quiet design", thumb: ["#8A857C", "#F6F4EC"] },
];

export const brandKit: BrandKit = {
  colors: [
    { name: "Canvas", hex: "#FBFAF6" },
    { name: "Ink", hex: "#1A1815" },
    { name: "Coral", hex: "#B84422" },
    { name: "Gold", hex: "#C8923D" },
  ],
  fonts: [
    { role: "Display & UI", family: "Geist" },
    { role: "Editorial", family: "Newsreader italic" },
    { role: "Labels", family: "Geist Mono" },
  ],
  logoMark: "aigen Studio",
  voice: "Sentence case. Lead with the human outcome. Never \"unlock insights\".",
};

/* --------------------------------- You ------------------------------------ */
export const domains: Domain[] = [
  {
    domain: "aigen.solutions",
    status: "live",
    ssl: "active",
    primary: true,
    dns: [
      { type: "A", name: "@", value: "76.76.21.21", ok: true },
      { type: "CNAME", name: "www", value: "cname.aigen.studio", ok: true },
    ],
  },
  {
    domain: "studio.aigen.solutions",
    status: "live",
    ssl: "active",
    primary: false,
    dns: [{ type: "CNAME", name: "studio", value: "cname.aigen.studio", ok: true }],
  },
  {
    domain: "meet-nara.com",
    status: "pending",
    ssl: "provisioning",
    primary: false,
    dns: [
      { type: "A", name: "@", value: "76.76.21.21", ok: true },
      { type: "CNAME", name: "www", value: "cname.aigen.studio", ok: false },
    ],
  },
];

export const account: Account = {
  user: { name: "Sam Ismayilov", email: "sam@aigen.solutions", initials: "SI" },
  workspace: { name: "aigen.solutions", plan: "Studio Scale" },
  plan: { name: "Studio Scale", price: "$149/mo", renews: "Jul 25, 2026" },
  usage: [
    { label: "Tracked events", used: 712, limit: 1000, unit: "k / mo" },
    { label: "Published pages", used: 12, limit: 50, unit: "pages" },
    { label: "Nara generations", used: 184, limit: 500, unit: "/ mo" },
    { label: "Team seats", used: 4, limit: 10, unit: "seats" },
  ],
  team: [
    { name: "Sam Ismayilov", email: "sam@aigen.solutions", role: "owner", initials: "SI" },
    { name: "Lena Park", email: "lena@aigen.solutions", role: "editor", initials: "LP" },
    { name: "Diego Romero", email: "diego@aigen.solutions", role: "editor", initials: "DR" },
    { name: "Aysel M.", email: "aysel@aigen.solutions", role: "viewer", initials: "AM" },
  ],
  integrations: [
    { name: "PostHog", status: "connected", desc: "Product analytics & session replay", source: "posthog" },
    { name: "Matomo", status: "connected", desc: "Privacy-first web analytics", source: "matomo" },
    { name: "MCP server", status: "connected", desc: "Expose Studio as tools for agents", source: "mcp" },
    { name: "Webhooks", status: "available", desc: "Stream events to your stack", source: "webhook" },
  ],
};

/* live "right now" baseline */
export const realtimeBase = 23;
export const realtimePages: Row[] = [
  { label: "/", value: 9, pct: 1 },
  { label: "/pricing", value: 6, pct: 0.66 },
  { label: "/studio", value: 4, pct: 0.44 },
  { label: "/nara", value: 3, pct: 0.33 },
  { label: "/blog/replay", value: 1, pct: 0.11 },
];
